import { all, call, fork, put, takeLatest, select } from 'redux-saga/effects';
import i18n from 'i18next';

import { FETCH_THRESHOLD_SETTINGS, SAVE_THRESHOLD_SETTINGS, FETCH_NOTIFICATION_SETTINGS, SAVE_NOTIFICATION_SETTINGS, SAVE_AUTO_SHUTOFF_SETTINGS } from '../constants/ActionTypes';

import { showError, clearMessage, showMessage } from '../actions/Common';
import { CustomAction } from 'model/state';
import { SettingsPayload, fetchThresholdSettingsSuccess, fetchThresholdSettingsError, saveThresholdSettingsSuccess, saveThresholdSettingsError, fetchNotificationSettingsSuccess, fetchNotificationSettingsError, saveNotificationSettingsSuccess, saveNotificationSettingsError } from 'actions';
import { Location, LocationType, MessageType, User, AlarmSettings, SystemMode, DefaultSettings, DeviceType, ThresholdSettings } from 'model';
import { getLocationById, setLocationFloSense } from 'api/Location';
import { toLocation, LocationResponse, UnitResponse, DeviceResponse, PESResponse, toThresholdSettings, AlarmsResponse, toDeviceType } from 'model/api';
import { saveAlarmSettings, getAlarmSettings } from 'api/User';
import { selectLoggedUser } from 'selectors';
import { AlarmSettingsResponse, toAlarmSettings, fromAlarmSettings, GetAlarmSettingsPayload, GetAlarmSettingsResponse } from 'model/api/AlarmSettings';
import { getAlarms } from 'api/Alarm';
import { selectDirtyAlarmSettings } from 'selectors/Settings';
import { analytics } from 'utils/Analytics';

const FLO_SENSE_OVERRIDE = 'FloSenseOverride';

function* fetchPESData(locations: Location[]): any {
  let locationList: Location[];
  let found = false;
  let locationQueue: Location[] = locations.filter(l => l.type !== LocationType.UNIT);
  let units = locations.filter(l => l.type === LocationType.UNIT);

  let pesData: PESResponse | null = yield call(searchDevices, units);
  if (pesData !== null) {
    found = true;
  }

  while (locationQueue.length > 0 && !found) {

    const currentLocation = locationQueue.shift();
    const parentData = yield call(getLocationById, currentLocation?.id || '', ['children']);
    locationList = parentData.children.map((location: LocationResponse) => toLocation(location));

    locationQueue = [
      ...locationQueue,
      ...locationList.filter(l => l.type !== LocationType.UNIT),
    ];
    units = locationList.filter(l => l.type === LocationType.UNIT);

    pesData = yield call(searchDevices, units);
    if (pesData !== null) {
      found = true;
    }
  }

  return pesData;
}

function* fetchThresholdsSettingsWorker(action: CustomAction<SettingsPayload>) {
  const locations = action.payload?.locations;
  try {
    if (locations === undefined) {
      throw Error('Invalid Parameters');
    }
    const pesData: PESResponse | null = yield call(fetchPESData, locations);
    yield put(fetchThresholdSettingsSuccess(toThresholdSettings(pesData)));
  } catch (err) {
    yield put(showError(i18n.t('connection_error'), err));
    yield put(fetchThresholdSettingsError());
  }
}

function* searchDevices(units: Location[]): any {
  for(let index = 0; index < units.length; index++) {
    const unit: UnitResponse = yield call(getLocationById, units[index].id || '', ['devices(pes)']);
    const deviceWithPES = unit.devices.find(device => toDeviceType((device as DeviceResponse).deviceType) === DeviceType.SHUTOFF && (device as DeviceResponse).pes);
    const devicePuckWithHWThreshold = unit.devices.find(device => toDeviceType((device as DeviceResponse).deviceType) === DeviceType.DETECTOR && (device as DeviceResponse).hardwareThresholds);
    if (deviceWithPES !== undefined) {
      const pesData = {
        schedule: {
          ...(deviceWithPES as DeviceResponse).pes?.schedule,
          items: (deviceWithPES as DeviceResponse).pes?.schedule.items.filter(item => item.name === FLO_SENSE_OVERRIDE && item.mode === 'home'),
          hardwareThresholds: {}
        },
      };
      
      if (devicePuckWithHWThreshold !== undefined) {
        pesData.schedule.hardwareThresholds = (devicePuckWithHWThreshold as DeviceResponse).hardwareThresholds
      }
      return pesData;
    } else if(devicePuckWithHWThreshold !== undefined) {
        const pesData = {
          schedule: {
            ...(devicePuckWithHWThreshold as DeviceResponse).pes?.schedule,
            items: (devicePuckWithHWThreshold as DeviceResponse).pes?.schedule.items.filter(item => item.name === FLO_SENSE_OVERRIDE && item.mode === 'home'),
            hardwareThresholds : (devicePuckWithHWThreshold as DeviceResponse).hardwareThresholds
          },        
        };      
      return pesData;
    }
  }
  return null;
}

function* savePESSettings(settings: ThresholdSettings, locationIds: string[], loggedUser: User): any {
  const apiCalls = [];
  for (let index = 0; index < locationIds.length; index++) {
    apiCalls.push(call(setLocationFloSense, locationIds[index], {
      duration: settings.duration.value * 60, // convert to seconds
      volume: settings.volume.value,
      flowRate: settings.flowRate.value,
      flowRateDuration: settings.flowRateDuration.value,
      shutoffDisabled: settings.shutoffDisabled,
    }));
    getLocationById(locationIds[index]).then(location => {
      analytics.trackThresholdUpdate(loggedUser?.id || '', location?.class?.key);
    }).catch(() => {});
  }
  yield all(apiCalls);
}

function* saveThresholdsSettingsWorker(action: CustomAction<SettingsPayload>) {
  const settings = action.payload?.settings;
  const locationIds = action.payload?.locationIds;
  const loggedUser: User = yield select(selectLoggedUser());
  try {
    if (settings === undefined || locationIds === undefined) {
      throw Error('Invalid Parameters');
    }
    yield put(clearMessage());
    yield call(savePESSettings, settings, locationIds, loggedUser);

    yield put(saveThresholdSettingsSuccess(settings));
    yield put(showMessage({ message: i18n.t('thresholds-saved-title'), description: i18n.t('thresholds-saved-description'), type: MessageType.SUCCESS }));
  } catch (err) {
    yield put(showError(i18n.t('connection_error'), err));
    yield put(saveThresholdSettingsError());
  }
}

function* fetchNotificationSettingsWorker(action: CustomAction<SettingsPayload>) {
  const deviceId = action.payload?.device?.id;
  const userIdParam = action.payload?.userId;
  const locationIds = action.payload?.locationIds;
  try {
    if (deviceId === undefined && locationIds === undefined) {
      throw Error('Invalid Parameters');
    }
    yield put(clearMessage());
    const loggedUser: User | undefined = yield select(selectLoggedUser());
    const userId = userIdParam ? userIdParam : loggedUser?.id || '';

    const payload: GetAlarmSettingsPayload = deviceId ? { deviceIds: [ deviceId ] } : { locationIds };
    const [alarmSettingsResponse, alarms]: [GetAlarmSettingsResponse, AlarmsResponse] = yield all([
      call(getAlarmSettings, userId, payload),
      call(getAlarms, loggedUser?.locale)
    ])
    const alarmSettings: AlarmSettingsResponse = alarmSettingsResponse.items || [];

    yield put(fetchNotificationSettingsSuccess(toAlarmSettings(alarmSettings, alarms)));
  } catch (err) {
    yield put(showError(i18n.t('connection_error'), err));
    yield put(fetchNotificationSettingsError());
  }
}

function isSupportedMode(systemMode: SystemMode, defaultSettings: DefaultSettings): boolean {
  return (
    (systemMode === SystemMode.HOME && defaultSettings.default.home.supported) ||
    (systemMode === SystemMode.AWAY && defaultSettings.default.away.supported) ||
    (systemMode === SystemMode.SLEEP && defaultSettings.default.sleep.supported)
  );
}

function* saveNotificationSettingsWorker(action: CustomAction<SettingsPayload>) {
  const userIdParam = action.payload?.userId;
  try {
    yield put(clearMessage());
    const loggedUser: User | undefined = yield select(selectLoggedUser());
    const userId = userIdParam ? userIdParam : loggedUser?.id || '';

    const dirtySettings: AlarmSettings[] = yield select(selectDirtyAlarmSettings());
    const settings = dirtySettings.map(datum => {
      return {
        ...datum,
        smsEnabled: isSupportedMode(datum.systemMode, datum.alarm.deliveryMediums.sms) ? datum.smsEnabled : undefined,
        emailEnabled: isSupportedMode(datum.systemMode, datum.alarm.deliveryMediums.email) ? datum.emailEnabled : undefined,
        pushEnabled: isSupportedMode(datum.systemMode, datum.alarm.deliveryMediums.push) ? datum.pushEnabled : undefined,
        callEnabled: isSupportedMode(datum.systemMode, datum.alarm.deliveryMediums.call) ? datum.callEnabled : undefined,
      }
    });

    yield call(saveAlarmSettings, userId, fromAlarmSettings(settings));
    yield put(showMessage({ message: i18n.t('alarm-settings-saved-title'), description: i18n.t('alarm-settings-saved-description'), type: MessageType.SUCCESS }));

    yield put(saveNotificationSettingsSuccess());
  } catch (err) {
    yield put(showError(i18n.t('connection_error'), err));
    yield put(saveNotificationSettingsError());
  }
}

export function* fetchThresholdsSettingsSaga() {
  yield takeLatest(FETCH_THRESHOLD_SETTINGS, fetchThresholdsSettingsWorker);
}

export function* saveThresholdsSettingsSaga() {
  yield takeLatest(SAVE_THRESHOLD_SETTINGS, saveThresholdsSettingsWorker);
}

export function* fetchNotificationSettingsSaga() {
  yield takeLatest(FETCH_NOTIFICATION_SETTINGS, fetchNotificationSettingsWorker);
}

export function* saveNotificationSettingsSaga() {
  yield takeLatest(SAVE_NOTIFICATION_SETTINGS, saveNotificationSettingsWorker);
}

function* saveAutoShutoffSettingsWorker(action: CustomAction<SettingsPayload>) {
  const settings = action.payload?.settings;
  const locationIds = action.payload?.locationIds;
  const loggedUser: User = yield select(selectLoggedUser());
  try {
    if (settings === undefined || locationIds === undefined) {
      throw Error('Invalid Parameters');
    }
    yield put(clearMessage());
    yield call(savePESSettings, settings, locationIds, loggedUser);

    yield put(saveThresholdSettingsSuccess(settings));
    yield put(showMessage({ message: i18n.t('auto-shutoff-saved-title'), description: i18n.t('auto-shutoff-saved-description'), type: MessageType.SUCCESS }));
  } catch (err) {
    yield put(showError(i18n.t('connection_error'), err));
    yield put(saveThresholdSettingsError());
  }
}

export function* saveAutoShutoffSettingsSaga() {
  yield takeLatest(SAVE_AUTO_SHUTOFF_SETTINGS, saveAutoShutoffSettingsWorker);
}

export default function* settingsSagas() {
  yield all([
    fork(fetchThresholdsSettingsSaga),
    fork(saveThresholdsSettingsSaga),
    fork(fetchNotificationSettingsSaga),
    fork(saveNotificationSettingsSaga),
    fork(saveAutoShutoffSettingsSaga),
  ]);
}
